<ion-content style="--padding-top: 48px; --background: #f4f5f8;">

  <div class="settings-wrap">

    <!-- Page header -->
    <div class="settings-header">
      <div>
        <h1 class="settings-title">{{ 'SETTINGS' | translate }}</h1>
        <p class="settings-subtitle">{{ 'USER_DATA.YOUR_DATA' | translate }}</p>
      </div>

      <div class="settings-actions">
        <button *ngIf="!editMode"
                type="button"
                (click)="toggleEdit()"
                class="s-btn s-btn-primary">
          <i class="fa-solid fa-pen"></i>
          {{ 'USER_DATA.EDIT_INFORMATION' | translate }}
        </button>
        <button *ngIf="editMode"
                type="button"
                (click)="cancelEdit()"
                class="s-btn s-btn-ghost">
          {{ 'CANCEL' | translate }}
        </button>
        <button *ngIf="editMode"
                type="button"
                (click)="save()"
                class="s-btn s-btn-primary">
          <i class="fa-solid fa-check"></i>
          {{ 'SAVE' | translate }}
        </button>
      </div>
    </div>

    <form [formGroup]="userForm">

      <!-- Profile card -->
      <section class="s-card">
        <div class="s-profile">
          <div class="s-avatar">{{ (userData?.name || userData?.email || '?') | slice:0:1 | uppercase }}</div>
          <div class="s-profile-meta">
            <span class="s-profile-name">{{ userData?.name || ('USER_DATA.INFORMATION' | translate) }}</span>
            <span class="s-profile-email">{{ userData?.email }}</span>
          </div>
        </div>

        <div class="s-divider"></div>

        <h2 class="s-section-title">{{ 'USER_DATA.INFORMATION' | translate }}</h2>

        <div class="s-grid">
          <!-- Email -->
          <div class="s-field">
            <label class="s-label">Email</label>
            <input type="email"
                   formControlName="email"
                   class="s-input s-input-readonly">
          </div>

          <!-- Name -->
          <div class="s-field">
            <label class="s-label">{{ 'NAME' | translate }}</label>
            <input type="text"
                   placeholder="{{ 'NAME' | translate }}"
                   formControlName="name"
                   class="s-input"
                   [class.is-edit]="editMode">
            <div *ngIf="errorControl['name']?.invalid && (errorControl['name']?.dirty || errorControl['name']?.touched)"
                 class="s-error">
              <span *ngIf="errorControl['name']?.errors?.['required']">{{ 'NAME_REQUIRED' | translate }}</span>
              <span *ngIf="errorControl['name']?.errors?.['minlength'] || errorControl['name']?.errors?.['maxlength']">{{ 'NAME_LENGTH_INVALID' | translate }}</span>
            </div>
          </div>

          <!-- Phone -->
          <div class="s-field">
            <label class="s-label">{{ 'PHONE' | translate }}</label>
            <input type="text"
                   placeholder="{{ 'PHONE' | translate }}"
                   formControlName="phone"
                   class="s-input"
                   [class.is-edit]="editMode">
            <div *ngIf="errorControl['phone']?.invalid && (errorControl['phone']?.dirty || errorControl['phone']?.touched)"
                 class="s-error">
              <span *ngIf="errorControl['phone']?.errors?.['required']">{{ 'PHONE_REQUIRED' | translate }}</span>
              <span *ngIf="errorControl['phone']?.errors?.['pattern']">{{ 'PHONE_PATTERN_INVALID' | translate }}</span>
              <span *ngIf="errorControl['phone']?.errors?.['minlength'] || errorControl['phone']?.errors?.['maxlength']">{{ 'PHONE_LENGTH_INVALID' | translate }}</span>
            </div>
          </div>

          <!-- Country -->
          <div class="s-field" #triggerInput>
            <label class="s-label">{{ 'COUNTRY' | translate }}</label>
            <input type="text"
                   placeholder="{{ 'COUNTRY' | translate }}"
                   formControlName="country"
                   (click)="onExtensionSelectorClick($event)"
                   (input)="setSearchTerm($event)"
                   id="countryInput"
                   class="s-input"
                   [class.is-edit]="editMode">
            <div *ngIf="errorControl['country']?.invalid && (errorControl['country']?.dirty || errorControl['country']?.touched)"
                 class="s-error">
              <span *ngIf="errorControl['country']?.errors?.['required']">{{ 'COUNTRY_REQUIRED' | translate }}</span>
              <span *ngIf="errorControl['country']?.errors?.['minlength'] || errorControl['country']?.errors?.['maxlength']">{{ 'COUNTRY_LENGTH_INVALID' | translate }}</span>
              <span *ngIf="errorControl['country']?.errors?.['invalidCountryName']">{{ 'COUNTRY_INVALID_NAME' | translate }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Email preferences card -->
      <section class="s-card">
        <h2 class="s-section-title">{{ 'USER_DATA.EMAIL_PREFERENCES' | translate }}</h2>

        <div class="s-pref">
          <div class="s-pref-info">
            <div class="s-pref-icon"><i class="fa-solid fa-envelope"></i></div>
            <div>
              <div class="s-pref-title">{{ 'USER_DATA.MARKETING_EMAILS' | translate }}</div>
              <div class="s-pref-desc">{{ 'USER_DATA.MARKETING_EMAILS_DESC' | translate }}</div>
            </div>
          </div>
          <label class="s-switch">
            <input type="checkbox"
                   [checked]="!userData?.unsubscribedEmailMarketing"
                   (change)="toggleEmailMarketing()">
            <span class="s-slider"></span>
          </label>
        </div>
        <p class="s-note">{{ 'USER_DATA.TRANSACTIONAL_EMAILS_NOTE' | translate }}</p>
      </section>

      <!-- API notifications card -->
      <section class="s-card">
        <h2 class="s-section-title">{{ 'vcaas.emailNotifications' | translate }}</h2>
        <p class="s-note !mt-0 mb-4">{{ 'vcaas.emailNotificationsHint' | translate }}</p>

        <div class="s-pref mb-3">
          <div class="s-pref-info">
            <div class="s-pref-icon"><i class="fa-solid fa-flag-checkered"></i></div>
            <div>
              <div class="s-pref-title">{{ 'vcaas.notifyFirstPrompt' | translate }}</div>
              <div class="s-pref-desc">{{ 'vcaas.notifyFirstPromptDesc' | translate }}</div>
            </div>
          </div>
          <label class="s-switch">
            <input type="checkbox"
                   [(ngModel)]="emailOnFirstPromptCompleted"
                   [ngModelOptions]="{ standalone: true }"
                   (change)="saveNotificationPreferences()">
            <span class="s-slider"></span>
          </label>
        </div>

        <div class="s-pref">
          <div class="s-pref-info">
            <div class="s-pref-icon"><i class="fa-solid fa-gauge-high"></i></div>
            <div>
              <div class="s-pref-title">{{ 'vcaas.notifyLimitReached' | translate }}</div>
              <div class="s-pref-desc">{{ 'vcaas.notifyLimitReachedDesc' | translate }}</div>
            </div>
          </div>
          <label class="s-switch">
            <input type="checkbox"
                   [(ngModel)]="emailOnLimitReached"
                   [ngModelOptions]="{ standalone: true }"
                   (change)="saveNotificationPreferences()">
            <span class="s-slider"></span>
          </label>
        </div>

        <p *ngIf="savingNotifications" class="text-xs text-indigo-500 mt-3">{{ 'vcaas.saving' | translate }}</p>
      </section>

      <!-- Actions card -->
      <section class="s-card s-card-actions">
        <button class="s-btn s-btn-primary" type="button" (click)="manageBilling()">
          <i class="fa-solid fa-file-invoice"></i>
          {{ 'USER_DATA.INVOICES_AND_BILLING' | translate }}
        </button>
        <button class="s-btn s-btn-danger" type="button" (click)="logout()">
          <i class="fa-solid fa-arrow-right-from-bracket"></i>
          {{ 'LOGOUT' | translate }}
        </button>
      </section>

    </form>
  </div>
</ion-content>

<!-- Country selector tooltip -->
<div #tooltip class="custom-tooltip" id="customTooltip" *ngIf="tooltipVisible" [ngStyle]="{'top': tooltipPosition.top, 'left': tooltipPosition.left, 'width': tooltipPosition.width}">
  <app-modal-country-selector (onSelect)="onSelectCountry($event)" [searchTerm]="searchTerm"></app-modal-country-selector>
</div>
